import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardContent,
  Slide,
  Stack
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import RetailerContext from '../context';
import { hexToRgb, isLight } from '../utils/colour';
import Wysiwyg from './Wysiwyg';
import { pushToGoogleAnalytics } from '../utils/google-analytics';

const CallToActionMarquee = ({
  title = '',
  message = '',
  dateUpdated = '',
  ctaLink = '',
  ctaText = '',
  delay = 5000,
  storageKey = 'cta-marquee-dismissed',
  bannerImage,
  bannerAlt = 'Canada Beef Call to Action'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const retailerContext = useContext(RetailerContext);
  const history = useHistory();

  useEffect(() => {
    // Check if marquee was previously dismissed for this dateUpdated
    const storedDateUpdated = localStorage.getItem(storageKey);
    const wasDismissed = storedDateUpdated === dateUpdated;

    if (!wasDismissed) {
      // Show marquee after specified delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [delay, storageKey, dateUpdated]);

  const handleClose = () => {
    setIsClosing(true);

    // Store dismissal in localStorage with dateUpdated
    localStorage.setItem(storageKey, dateUpdated);

    // Track close
    pushToGoogleAnalytics({
      event: 'callToActionMarqueeDismissed',
      title: 'CallToActionMarquee',
      CTAName: title,
    });

    // Remove element after animation completes
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 800);
  };

  const closeMarquee = () => {
    setIsClosing(true);

    // Store dismissal in localStorage with dateUpdated
    localStorage.setItem(storageKey, dateUpdated);

    // Remove element after animation completes
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 800);
  };

  const handleCTAClick = () => {
    // Track CTA click
    pushToGoogleAnalytics({
      event: 'callToActionMarqueeClicked',
      title: 'CallToActionMarquee',
      CTAName: title,
    });

    // Close the marquee without triggering dismissed event
    closeMarquee();

    // Navigate after a short delay to allow the close animation to start
    setTimeout(() => {
      history.push(ctaLink);
    }, 100);
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Get retailer brand color or fallback to default
  const brandColor = retailerContext?.retailer?.brand_color || '#ffffff';
  const isLightBrand = isLight(hexToRgb(brandColor));

  return (
    <Slide direction="up" in={!isClosing} timeout={800}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1000,
        }}
      >
        <Stack
          sx={{
            display: { md: 'none' },
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              ml: 'auto',
              borderRadius: '10px 10px 0 0',
              mr: 1,
              color: isLightBrand ? '#000' : '#fff',
              background: `${brandColor}`,
              '&:hover': {
                opacity: 1,
                backgroundColor: isLightBrand ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease',
              zIndex: 1000,
            }}
            aria-label="Close marquee"
          >
            <Cancel />
          </IconButton>
        </Stack>

        <Card
          sx={{
            height: '100%',
            background: `${brandColor}`,
            borderRadius: 0,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <CardContent
            sx={{
              position: 'relative',
              p: { xs: 2, sm: 3 },
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <IconButton
              onClick={handleClose}
              size="large"
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: 0,
                right: 24,
                color: isLightBrand ? '#000' : '#fff',
                background: `${brandColor}`,
                '&:hover': {
                  opacity: 1,
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease',
                zIndex: 1000,
              }}
              aria-label="Close marquee"
            >
              <Cancel
                sx={{
                  fontSize: '2rem',
                }}
              />
            </IconButton>

            <Stack
              direction="column"
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{
                width: '100%',
              }}
            >
              {bannerImage && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '90%', sm: 400 },
                    height: 'auto',
                    minHeight: { xs: 50, sm: 60, md: 70 },
                    maxHeight: { xs: 70, sm: 80, md: 90 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: { xs: 1, sm: 2 },
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    component="img"
                    src={bannerImage}
                    alt={bannerAlt}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </Box>
              )}

              <Typography
                sx={{
                  color: isLightBrand ? '#000' : '#fff',
                  textAlign: 'center',
                  px: { xs: 1, sm: 2 },
                  lineHeight: 1.5,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  maxWidth: '90%',
                  textShadow: isLightBrand ? '0 1px 2px rgba(255, 255, 255, 0.5)' : '0 1px 2px rgba(0, 0, 0, 0.5)'
                }}
              >
                <Wysiwyg>
                  {message}
                </Wysiwyg>
              </Typography>

              {ctaLink && (
                <Button
                  onClick={handleCTAClick}
                  variant="contained"
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    color: '#000',
                    px: 5,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    boxShadow: `0 4px 0 #000, 0 6px 8px rgba(0, 0, 0, 0.2)`,
                    '&:hover': {
                      backgroundColor: '#fff',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 0 #000, 0 8px 12px rgba(0, 0, 0, 0.3)`
                    },
                    '&:active': {
                      transform: 'translateY(2px)',
                      boxShadow: `0 2px 0 #000, 0 4px 6px rgba(0, 0, 0, 0.2)`
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {ctaText}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Slide>
  );
};

CallToActionMarquee.propTypes = {
  title: PropTypes.string,
  dateUpdated: PropTypes.string,
  message: PropTypes.string,
  ctaLink: PropTypes.string,
  ctaText: PropTypes.string,
  delay: PropTypes.number,
  storageKey: PropTypes.string,
  bannerImage: PropTypes.string,
  bannerAlt: PropTypes.string
};

export default CallToActionMarquee; 